import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import Wordmark from "@/components/Wordmark";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, FileText, ShieldCheck, Users, Package, Settings, LogOut, Bell, Menu, X, ExternalLink, AlertTriangle, Inbox,
} from "lucide-react";

const items = [
  { to: "/panel/dashboard", label: "Genel Bakış", icon: LayoutDashboard, roles: ["admin", "staff"] },
  { to: "/panel/requests", label: "Talepler", icon: FileText, roles: ["admin", "staff"] },
  { to: "/panel/policies", label: "Poliçeler", icon: ShieldCheck, roles: ["admin", "staff"] },
  { to: "/panel/products", label: "Ürünler", icon: Package, roles: ["admin"] },
  { to: "/panel/staff", label: "Personel", icon: Users, roles: ["admin"] },
  { to: "/panel/settings", label: "Site Ayarları", icon: Settings, roles: ["admin"] },
];

export default function PanelLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [notif, setNotif] = useState({ expiring_policies: [], new_requests: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setNotif(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const doLogout = () => {
    logout();
    nav("/panel");
  };

  const visible = items.filter((i) => i.roles.includes(user?.role));
  const expiringCount = notif.expiring_policies?.length || 0;
  const totalNotif = notif.new_requests + expiringCount;

  return (
    <div className="min-h-screen flex" style={{ background: "var(--asg-bg)" }}>
      <aside className={`fixed lg:sticky top-0 h-screen w-64 shrink-0 border-r border-[color:var(--asg-border)] z-40 transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} style={{ background: "var(--asg-surface)" }}>
        <div className="p-5 border-b border-[color:var(--asg-border)] flex items-center justify-between">
          <Wordmark size="sm" testid="panel-wordmark" />
          <button className="lg:hidden text-black/60" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 border-b border-[color:var(--asg-border)]">
          <div className="rounded-lg bg-white p-3 border border-[color:var(--asg-border)]">
            <div className="text-xs text-black/60">Oturum</div>
            <div className="font-semibold text-black text-sm mt-0.5">{user?.name || user?.username}</div>
            <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--asg-primary-dark)" }}>{user?.role === "admin" ? "Yönetici" : "Personel"}</div>
          </div>
        </div>
        <nav className="p-3 grid gap-1">
          {visible.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "text-black shadow-sm"
                    : "text-black/70 hover:text-black hover:bg-white/70"
                }`
              }
              style={({ isActive }) => isActive ? {
                background: "linear-gradient(135deg, rgba(212,175,55,0.22), rgba(183,110,121,0.10))",
                border: "1px solid rgba(212,175,55,0.4)",
              } : {}}
              data-testid={`nav-panel-${i.to.split("/").pop()}`}
            >
              <i.icon className="w-4 h-4" /> {i.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 mt-auto absolute bottom-0 left-0 right-0 border-t border-[color:var(--asg-border)]">
          <Link to="/" className="flex items-center gap-2 px-3 py-2 text-sm text-black/70 hover:text-black">
            <ExternalLink className="w-4 h-4" /> Siteyi Görüntüle
          </Link>
          <button
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-black/70 hover:text-black"
            data-testid="panel-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Çıkış Yap
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="glass sticky top-0 z-30">
          <div className="h-16 px-5 lg:px-8 flex items-center justify-between">
            <button className="lg:hidden text-black" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
          <div className="hidden lg:block">
            <div className="text-xs uppercase tracking-widest text-black/60">Panel</div>
            <div className="font-heading text-lg text-black">{settings.site_name}</div>
          </div>
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative p-2 rounded-full hover:bg-black/5 transition-colors focus:outline-none"
                    data-testid="panel-notifications-btn"
                    aria-label="Bildirimler"
                  >
                    <Bell className="w-5 h-5 text-black/70" />
                    {totalNotif > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full text-[10px] px-1 flex items-center justify-center text-white font-semibold"
                        style={{ background: "var(--asg-primary-dark)" }}
                        data-testid="panel-notifications-badge"
                      >
                        {totalNotif}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-80 bg-white border-[color:var(--asg-border)] text-black"
                  data-testid="panel-notifications-menu"
                >
                  <DropdownMenuLabel className="font-heading text-base text-black">Bildirimler</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[color:var(--asg-border)]" />
                  {notif.new_requests > 0 && (
                    <DropdownMenuItem
                      onClick={() => nav("/panel/requests")}
                      className="focus:bg-[color:var(--asg-surface)] focus:text-black cursor-pointer py-3"
                      data-testid="notif-new-requests"
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(212,175,55,0.18)" }}>
                          <Inbox className="w-4 h-4" style={{ color: "var(--asg-primary-dark)" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-black">{notif.new_requests} yeni talep</div>
                          <div className="text-xs text-black/60 mt-0.5">İncelemek için tıkla</div>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {(notif.expiring_policies || []).slice(0, 6).map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      onClick={() => nav("/panel/policies")}
                      className="focus:bg-[color:var(--asg-surface)] focus:text-black cursor-pointer py-3"
                      data-testid={`notif-expiring-${p.id}`}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(245,158,11,0.15)" }}>
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-black truncate">{p.customer_name}</div>
                          <div className="text-xs text-black/60 mt-0.5 truncate">
                            {p.product_title} · {p.days_left < 0 ? `${Math.abs(p.days_left)} gün önce bitti` : `${p.days_left} gün kaldı`}
                          </div>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  {totalNotif === 0 && (
                    <div className="px-3 py-8 text-center text-sm text-black/50" data-testid="notif-empty">
                      Yeni bildirim yok
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-semibold text-black">{user?.name || user?.username}</span>
                <span className="text-[10px] uppercase tracking-widest text-black/60">{user?.role}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6 lg:p-8">
          <Outlet context={{ notif }} />
        </main>
      </div>
    </div>
  );
}
